import { themesMixin } from '@/utils/themes';
import { mapActions, mapState, mapMutations, mapGetters } from 'vuex';
export default {
  name: 'ViewLogin',
  directives: {
    disabledIconFocus: {
      bind(el) {
        el.querySelectorAll('.v-input__icon button').forEach(x => x.setAttribute('tabindex', -1));
      }
    }
  },
  mixins: [themesMixin],
  data: function () {
    return {
      showPage: false,
      valid: false,
      email: '',
      password: '',
      marker: false,
      tenantName: null,
      emailRules: [v => !!v, v => /.+@.+\..+/.test(v)],
      passwordRules: [v => !!v],
      submitted: false,
      homeRedirect: '/home',
      responsive: false,
      responsiveInput: false,
      showMfaInput: false,
      mfaToken: null,
      mfaSetupDialogOpen: false
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color', 'CustomTheme', 'UiTheme', 'loading']),
    ...mapGetters('authentication', ['hasPendingTwoFactorSetup']),
    loginLogo() {
      return this.themeGetLogoObject();
    },
    logoDefaultJustify() {
      if (this.logoPosition() === 'right') {
        return 'end';
      }
      if (this.logoPosition() === 'center') {
        return 'center';
      }
      return 'start';
    },
    loginLogoCenteredStyle() {
      if (this.isDefaultSvgLogo()) {
        return 'width: 134px; height: 40px;';
      }
      return `padding: 5px; width: ${this.loginLogo.orientation === 'square' ? 120 : 210}px; height: 120px;`;
    },
    loginBackground() {
      return this.themeGetBackground();
    },
    redirect() {
      if (this.$route.query.redirect) {
        return this.$route.query.redirect;
      }
      return null;
    },
    isOauth() {
      return this.$route.query.oauth ? true : false;
    },
    oauthSubdomainRedirectHref() {
      const host = window.location.hostname;
      const href = window.location.href;
      const lastLoginSubdomain = this.$store.state.app.lastLoginSubdomain;
      if (lastLoginSubdomain && lastLoginSubdomain !== host) {
        const url = href.replace(host, lastLoginSubdomain);
        return url;
      }
      return null;
    }
  },
  mounted() {
    // first attempt of loading, should have requested correct theming by now
    this.homeRedirect = this.$store.state.app.sidebarIconLink || '/home';
    document.getElementsByTagName('html')[0].classList.add('hide__scrollbar');
    window.addEventListener('message', this.handleIframeMessage);
    if (this.isOauth && this.oauthSubdomainRedirectHref) {
      window.location.href = this.oauthSubdomainRedirectHref;
      return;
    }
    if (this.$route.query.error) {
      this.showPage = true;
      switch (this.$route.query.error) {
        case 'oauth_user':
          this.$snack(this.$i18n_t('Common.errorOauthUserNotFound'), 'error');
          break;
      }
    } else {
      this.verify().then(() => {
        if (this.openSetupDialogIfRequired()) {
          return;
        }
        if (this.isOauth && this.redirect) {
          // check if redirect is a valid url e.g. not javascript injection
          if (!this.isValidRedirect(this.redirect)) {
            console.error('Invalid redirect URL:', this.redirect);
            this.$snack(this.$i18n_t('Common.invalidRedirect') || 'Invalid redirect URL', 'error');
            this.$router.push('/home');
            return;
          }

          // prevent infinite loops when user just pasted a URL with oauth=1
          if (this.shouldBlockOauthRedirect(this.redirect)) {
            const q = {
              ...this.$route.query
            };
            delete q.oauth;
            this.$router.replace({
              query: q
            });
            this.showPage = true;
            return;
          }
          this.markOauthRedirectAttempt(this.redirect);
          window.location.href = this.redirect;
          return;
        }
        if (this.redirect) {
          this.$router.push(this.redirect);
          return;
        }
        this.$snack(this.$i18n_t('Common.welcomeBack'), 'success');
        this.$router.push(this.homeRedirect);
      }).catch(() => {
        // Do nothing
        this.showPage = true;
      });
    }
    this.onResponsiveInverted();
    window.addEventListener('resize', this.onResponsiveInverted);
  },
  beforeDestroy() {
    document.getElementsByTagName('html')[0].classList.remove('hide__scrollbar');
    window.removeEventListener('resize', this.onResponsiveInverted);
    window.removeEventListener('message', this.handleIframeMessage);
  },
  methods: {
    ...mapActions('authentication', ['login', 'verify', 'logout']),
    ...mapMutations('app', ['snackbar']),
    openSetupDialogIfRequired() {
      if (!this.hasPendingTwoFactorSetup) {
        this.mfaSetupDialogOpen = false;
        return false;
      }
      this.showPage = true;
      this.mfaSetupDialogOpen = true;
      return true;
    },
    validate() {
      this.valid = this.$refs.form.validate();
      if (this.valid) {
        this.snackbar = true;
      }
    },
    isValidRedirect(redirect) {
      if (!redirect) {
        return false;
      }

      // vue router relative path check
      if (redirect.startsWith('/')) {
        try {
          const resolved = this.$router.resolve(redirect);
          return resolved && resolved.route && resolved.route.path;
        } catch (error) {
          return false;
        }
      }

      // for absolute URLs, validate protocol
      try {
        const url = new URL(redirect);
        return url.protocol === 'http:' || url.protocol === 'https:';
      } catch (error) {
        return false;
      }
    },
    handleIframeMessage(event) {
      if (event.data.lastLoginSubdomain) {
        this.$store.commit('app/setField', {
          field: 'lastLoginSubdomain',
          value: event.data.lastLoginSubdomain
        });
      }
    },
    handleSubmit() {
      // for double good measure, re-load the sidebar-home-link here, if it exists
      this.homeRedirect = this.$store.state.app.sidebarIconLink || '/home';
      this.$store.state.app.load = 10;
      this.$store.state.app.loading = true;
      this.validate();
      this.submitted = true;
      const {
        email,
        password,
        mfaToken,
        $router
      } = this;
      this.login({
        ...{
          username: email,
          password
        },
        ...(mfaToken && {
          token: mfaToken
        })
      }).then(() => {
        this.$snack(this.$i18n_t('Common.loggedIn'), 'success');
        this.$store.state.app.load = 100;
        this.$store.state.app.loading = false;
        if (this.openSetupDialogIfRequired()) {
          return;
        }
        if (this.isOauth && this.redirect) {
          // check if redirect is a valid url e.g. not javascript:alert('xss') or data:image/png;base64,i...
          if (!this.isValidRedirect(this.redirect)) {
            console.error('Invalid redirect URL:', this.redirect);
            this.$snack(this.$i18n_t('Common.invalidRedirect') || 'Invalid redirect URL', 'error');
            this.$router.push('/home');
            return;
          }

          // prevent infinite loops when user just pasted a URL with oauth=1
          if (this.shouldBlockOauthRedirect(this.redirect)) {
            const q = {
              ...this.$route.query
            };
            delete q.oauth;
            this.$router.replace({
              query: q
            });
            this.showPage = true;
            return;
          }
          this.markOauthRedirectAttempt(this.redirect);
          window.location.href = this.redirect;
          return;
        }
        if (this.redirect) {
          // check if redirect is a valid url e.g. not javascript:alert('xss') or data:image/png;base64,i...
          if (!this.isValidRedirect(this.redirect)) {
            console.error('Invalid redirect URL:', this.redirect);
            this.$snack(this.$i18n_t('Common.invalidRedirect') || 'Invalid redirect URL', 'error');
            $router.push('/home');
            return;
          }
          $router.push(this.redirect);
          return;
        }
        $router.push(this.homeRedirect);
      }).catch(error => {
        this.$store.state.app.load = 100;
        this.$store.state.app.loading = false;
        if (error.data && error.data.message === '2FA_TOKEN_REQUIRED') {
          this.showMfaInput = true;
          return;
        } else if (error.data && this.$i18n_t(`Error.${error.data.message}`)) {
          this.$snack(this.$i18n_t(`Error.${error.data.message}`), 'error');
        } else if (error.data && !this.$i18n_t(`Error.${error.data.message}`)) {
          this.$snack(this.$i18n_t('Error.unknown'), 'error');
        } else {
          this.$snack(this.$i18n_t('Common.credWrong'), 'error');
        }
      });
    },
    async handleSetupCanceled() {
      this.mfaSetupDialogOpen = false;
      await this.logout({
        reload: false
      });
      this.showMfaInput = false;
      this.mfaToken = null;
      this.$router.replace('/login');
      this.showPage = true;
    },
    async handleSetupCompleted() {
      try {
        await this.verify();
      } catch (e) {
        var _this$$store;
        const hasAuthenticatedUser = !!((_this$$store = this.$store) !== null && _this$$store !== void 0 && (_this$$store = _this$$store.state) !== null && _this$$store !== void 0 && (_this$$store = _this$$store.authentication) !== null && _this$$store !== void 0 && (_this$$store = _this$$store.user) !== null && _this$$store !== void 0 && _this$$store.id);
        if (!hasAuthenticatedUser) {
          this.mfaSetupDialogOpen = false;
          this.showMfaInput = false;
          this.mfaToken = null;
          this.showPage = true;
          this.$router.replace('/login');
        }
        this.$snack(this.$i18n_t('Common.multiFactorAuth.verifyToken_genericError'), 'error');
        return;
      }
      if (this.hasPendingTwoFactorSetup) {
        return;
      }
      this.mfaSetupDialogOpen = false;
      if (this.redirect && this.isValidRedirect(this.redirect)) {
        this.$router.push(this.redirect);
        return;
      }
      this.$router.push(this.homeRedirect);
    },
    onResponsiveInverted() {
      if (window.innerWidth < 991) {
        this.responsive = true;
        this.responsiveInput = false;
      } else {
        this.responsive = false;
        this.responsiveInput = true;
      }
    },
    shouldBlockOauthRedirect(redirect) {
      try {
        const key = 'oauth.redirect.guard';
        const raw = window.sessionStorage.getItem(key);
        if (!raw) {
          return false;
        }
        const data = JSON.parse(raw);
        if (!data || data.redirect !== redirect) {
          return false;
        }
        const now = Date.now();
        // Block if the same redirect was attempted in the last 10 seconds
        if (now - (data.ts || 0) < 10000 || (data.count || 0) >= 1) {
          return true;
        }
        return false;
      } catch (e) {
        return false;
      }
    },
    markOauthRedirectAttempt(redirect) {
      try {
        const key = 'oauth.redirect.guard';
        const raw = window.sessionStorage.getItem(key);
        const prev = raw ? JSON.parse(raw) : null;
        const next = {
          redirect,
          ts: Date.now(),
          count: prev && prev.redirect === redirect ? (prev.count || 0) + 1 : 1
        };
        window.sessionStorage.setItem(key, JSON.stringify(next));
      } catch (e) {
        // ignore
      }
    }
  }
};